import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import IconSvg from 'src/components/common/ui/IconSvg';
import { AuthContext } from 'src/contexts';
import { useHttp, useNotification } from 'src/hooks';
import { setToLocalStorage } from 'src/utils';

const SigninForm: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, setAccessToken } = useContext(AuthContext);

  const { sendRequest: apiRequestHandler, error } = useHttp();
  const notify = useNotification();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Function to validate all fields on submit
  const validateFields = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format.';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input change and clear errors on typing
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear errors for this field when user starts typing
    setErrors({ ...errors, [name]: '' });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  // Your login function
  const login = (payload: { email: string; password: string }) => {
    apiRequestHandler(
      {
        method: 'POST',
        url: 'auth/login',
        accessToken,
        payload: {
          email: payload.email,
          password: payload.password,
        },
      },
      (data: any) => {
        notify('Signin successful!', 'success');
        setToLocalStorage('::@@adlyJwtAuthspajs@@::', data); // todo  need to change
        setAccessToken(data.accessToken);
        navigate('/dashboard');
      },
    );
  };

  // Handle form submit with validation
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateFields()) return; // Stop if validation fails

    setIsSubmitting(true);

    try {
      // Use your login function here instead of the mock API
      login(formData);
    } catch (error) {
      notify('Signin failed. Please check your credentials.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    if (error) {
      notify(error || '', 'error');
    }
  }, [error]);
  return (
    <div className="lg:w-1/2 w-full lg:p-8 flex flex-col items-start">
      <ToastContainer />

      <IconSvg icon="appLogo" className="cursor-pointer lg:w-auto w-24" onClick={() => navigate('/')} alt="Logo" />
      <p className="lg:text-[36px] text-[28px] font-semibold text-black font-inter mt-8 lg:mb-6 mb-4">
        Sign in
      </p>
      <p className="text-[#4A4D4F] lg:text-xl text-base font-normal font-inter mb-6">
        Check-In on Your Campaigns
      </p>
      <form className="w-full" onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-[#4A4D4F] lg:text-lg text-base font-normal mb-2">
            Email <span className="text-[#CC0E3C]">*</span>
          </label>
          <input
            type="email"
            name="email"
            placeholder="Enter email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg placeholder-grayscale-600 lg:text-base text-sm font-inter font-normal outline-none ${errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>
        <div className="mb-4">
          <label className="block text-[#4A4D4F] lg:text-lg text-base font-normal mb-2">
            Password <span className="text-[#CC0E3C]">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="Enter password"
              value={formData.password}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg placeholder-grayscale-600 lg:text-base text-sm font-inter font-normal outline-none ${errors.password ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            <span
              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <IconSvg
                  icon="hidePasswordEye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              ) : (
                <IconSvg
                  icon="eye"
                  className="absolute inset-y-0 right-2.5 my-auto h-5 w-5 cursor-pointer"
                />
              )}
            </span>
          </div>
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
          <div className="mt-1 flex items-center justify-end text-black w-full text-center lg:text-base text-sm font-semibold">
            <p
              onClick={() => navigate('/forgot-password')}
              className="cursor-pointer underline underline-offset-4 text-[#6277FF]"
            >
              Forgot password?
            </p>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-2 rounded text-white ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
            }`}
        >
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      <div className="mt-4 flex items-center space-x-2 justify-center text-black w-full text-center lg:text-lg text-sm font-semibold">
        <p>Don’t have an account?</p>
        <p
          onClick={() => navigate('/signup')}
          className="cursor-pointer underline underline-offset-4 text-[#6277FF]"
        >
          Sign up
        </p>
      </div>
    </div>
  );
};

export default SigninForm;
